import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

import { KnowledgeSearchPageHit } from '@digital-twins/shared-types'
import Link from '@/routing/Link'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { knowledgeApi } from '@/lib/api'
import i18n from '@/i18n'
import { highlightQueryInSnippet } from './highlightQueryInSnippet'
import {
  pageHitKey,
  pageTimestampSeconds,
  sortTimelinePages,
  timelineMarkerPercent,
} from './searchPageTimeline'

interface KnowledgeSearchRelevantPagesProps {
  documentToken: string
  query: string
  pages: Array<KnowledgeSearchPageHit>
  pageHitCount?: number
  defaultExpanded?: boolean
  /** Cover or link thumbnail — used for video/audio context when no page raster exists. */
  coverImageUrl?: string | null
}

const timelineExtentCache = new Map<string, number>()

const TS_SPAN_HEADING_RE = /^#{2,3}\s*\[(\d{1,2}:\d{2}(?::\d{2})?)\]/

function cacheKey(documentToken: string, timestampMode: boolean): string {
  return `${documentToken}:${timestampMode ? 'media' : 'pages'}`
}

function timestampLabelFromPage(page: KnowledgeSearchPageHit): string | null {
  const fromLabel = (page.label || '').trim()
  if (fromLabel) {
    return fromLabel
  }
  const match = (page.snippet || '').match(TS_SPAN_HEADING_RE)
  return match?.[1] ?? null
}

function isTimestampHit(page: KnowledgeSearchPageHit): boolean {
  return page.unitKind === 'timestamp'
}

export function isTimestampSearchHit(pages: Array<KnowledgeSearchPageHit>): boolean {
  return pages.length > 0 && pages.every(isTimestampHit)
}

export function pageHitLocator(page: KnowledgeSearchPageHit): string {
  if (isTimestampHit(page)) {
    const label = timestampLabelFromPage(page)
    return i18n.t('library.timeLabel', {
      ns: 'app',
      label: label ?? String(page.unitIndex),
    })
  }
  if (page.unitKind === 'slide') {
    return page.label || i18n.t('library.slideN', { ns: 'app', n: page.unitIndex })
  }
  if (page.unitKind === 'sheet') {
    return page.label || i18n.t('library.sheetN', { ns: 'app', n: page.unitIndex })
  }
  return i18n.t('library.pageN', { ns: 'app', n: page.unitIndex })
}

export function pageHitHref(documentToken: string, page: KnowledgeSearchPageHit): string {
  if (isTimestampHit(page)) {
    const label = timestampLabelFromPage(page)
    if (label) {
      return `/knowledge/${documentToken}?t=${encodeURIComponent(label)}`
    }
  }
  return `/knowledge/${documentToken}?page=${page.unitIndex}`
}

function pagePreviewImageSrc(preview: {
  imageUrl?: string | null
  imageBase64?: string | null
}): string | null {
  if (preview.imageUrl?.trim()) {
    return preview.imageUrl.trim()
  }
  if (preview.imageBase64?.trim()) {
    const raw = preview.imageBase64.trim()
    return raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`
  }
  return null
}

function HighlightedSnippet(props: {
  snippet: string
  query: string
}) {
  const { snippet, query } = props
  const parts = highlightQueryInSnippet(snippet, query)

  return (
    <p className="line-clamp-3 min-h-[3.75rem] text-sm leading-relaxed text-foreground/90">
      {parts.map((part, index) =>
        part.match ? (
          <strong
            key={`${part.text}-${index}`}
            className="rounded-sm bg-primary/20 font-semibold text-foreground"
          >
            {part.text}
          </strong>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        ),
      )}
    </p>
  )
}

export function KnowledgeSearchRelevantPages(props: KnowledgeSearchRelevantPagesProps) {
  const {
    documentToken,
    query,
    pages,
    pageHitCount,
    defaultExpanded = false,
    coverImageUrl = null,
  } = props

  const { t } = useTranslation('app')
  const timestampMode = isTimestampSearchHit(pages)
  const extentCacheKey = cacheKey(documentToken, timestampMode)

  const [expanded, setExpanded] = useState(defaultExpanded)
  const [selectedKey, setSelectedKey] = useState(() =>
    pageHitKey(pages[0] ?? { unitKind: 'page', unitIndex: 0, snippet: '' }),
  )
  const [pagePreviewSrc, setPagePreviewSrc] = useState<string | null>(null)
  const [timelineExtent, setTimelineExtent] = useState<number | null>(
    () => timelineExtentCache.get(extentCacheKey) ?? null,
  )
  const [loadingTimeline, setLoadingTimeline] = useState(
    () => !timelineExtentCache.has(extentCacheKey),
  )

  const sortedPages = useMemo(
    () => sortTimelinePages(pages, timestampMode),
    [pages, timestampMode],
  )
  const count = pageHitCount ?? pages.length
  const selected =
    sortedPages.find((page) => pageHitKey(page) === selectedKey) ??
    pages[0] ??
    sortedPages[0]
  const href = selected ? pageHitHref(documentToken, selected) : `/knowledge/${documentToken}`
  const barLabel = timestampMode
    ? t('library.relevantHit', { count })
    : t('library.relevantPage', { count })
  const selectedPercent =
    selected && timelineExtent
      ? timelineMarkerPercent(selected, timelineExtent, timestampMode)
      : null

  useEffect(() => {
    if (pages[0]) {
      setSelectedKey(pageHitKey(pages[0]))
    }
  }, [pages])

  useEffect(() => {
    if (!expanded) {
      return
    }
    if (timelineExtentCache.has(extentCacheKey)) {
      setTimelineExtent(timelineExtentCache.get(extentCacheKey) ?? null)
      setLoadingTimeline(false)
      return
    }

    let cancelled = false
    setLoadingTimeline(true)
    void (async () => {
      try {
        const result = await knowledgeApi.getAllPagePreviews(
          documentToken,
          timestampMode ? undefined : { fromPage: 1, toPage: 1 },
        )
        if (cancelled) {
          return
        }
        const extent = timestampMode
          ? Math.max(
              result.pages.reduce((max, page) => {
                const hit: KnowledgeSearchPageHit = {
                  unitKind: 'timestamp',
                  unitIndex: page.pageNumber,
                  label: null,
                  snippet: page.text,
                }
                return Math.max(max, pageTimestampSeconds(hit))
              }, 0),
              1,
            )
          : Math.max(result.pageCount, 1)
        timelineExtentCache.set(extentCacheKey, extent)
        setTimelineExtent(extent)
      } catch {
        if (!cancelled) {
          setTimelineExtent(null)
        }
      } finally {
        if (!cancelled) {
          setLoadingTimeline(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [documentToken, expanded, extentCacheKey, timestampMode])

  useEffect(() => {
    if (!expanded || !selected || timestampMode || selected.unitKind !== 'page') {
      setPagePreviewSrc(null)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const preview = await knowledgeApi.getPagePreview(
          documentToken,
          selected.unitIndex,
        )
        if (cancelled) {
          return
        }
        setPagePreviewSrc(pagePreviewImageSrc(preview))
      } catch {
        if (!cancelled) {
          setPagePreviewSrc(null)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [documentToken, expanded, selected, timestampMode])

  if (pages.length === 0) {
    return null
  }

  const contextThumb = timestampMode
    ? coverImageUrl?.trim() || null
    : pagePreviewSrc || coverImageUrl?.trim() || null

  return (
    <div
      className="mt-1 border-t border-border"
      data-testid="knowledge-search-relevant-pages"
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 bg-primary/10 px-4 py-2.5 text-left transition-colors hover:bg-primary/15"
        aria-expanded={expanded}
        data-testid="knowledge-search-relevant-pages-toggle"
        onClick={() => setExpanded((open) => !open)}
      >
        <Text size="sm" className="font-medium text-foreground">
          {barLabel}
        </Text>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-3 px-4 pb-4 pt-4">
            {loadingTimeline || timelineExtent == null ? (
              <Skeleton
                className="h-10 w-full rounded-lg"
                data-testid="knowledge-search-timeline-loading"
              />
            ) : (
              <div data-testid="knowledge-search-page-picker">
                <div className="relative mx-3 h-6">
                  <div
                    className="pointer-events-none absolute inset-x-0 top-[9px] h-px bg-primary/30"
                    aria-hidden
                  />
                  {sortedPages.map((page) => {
                    const active = pageHitKey(page) === selectedKey
                    const leftPercent = timelineMarkerPercent(
                      page,
                      timelineExtent,
                      timestampMode,
                    )
                    return (
                      <button
                        key={pageHitKey(page)}
                        type="button"
                        style={{ left: `${leftPercent}%` }}
                        className={cn(
                          'absolute top-1.5 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 transition-colors',
                          active
                            ? 'border-primary bg-primary'
                            : 'border-primary/50 bg-background hover:border-primary/80',
                        )}
                        aria-label={pageHitLocator(page)}
                        aria-pressed={active}
                        data-testid="knowledge-search-page-hit"
                        data-active={active}
                        data-position={leftPercent.toFixed(1)}
                        onClick={() => setSelectedKey(pageHitKey(page))}
                      />
                    )
                  })}
                </div>
                {selectedPercent != null ? (
                  <div className="relative mx-3 mt-1 h-5">
                    <Text
                      size="xs"
                      className="absolute top-0 -translate-x-1/2 font-medium text-foreground"
                      style={{ left: `${selectedPercent}%` }}
                      data-testid="knowledge-search-page-hit-label"
                    >
                      {pageHitLocator(selected)}
                    </Text>
                  </div>
                ) : null}
              </div>
            )}

            <Link
              href={href}
              className="flex min-h-[6.5rem] gap-3 rounded-xl border border-border bg-muted/30 p-3 transition-colors hover:bg-muted/50"
              data-testid="knowledge-search-page-snippet"
            >
              <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md border border-border bg-muted/40">
                {contextThumb ? (
                  <img
                    src={contextThumb}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                    data-testid="knowledge-search-context-thumb"
                  />
                ) : (
                  <Skeleton className="h-full w-full rounded-none" />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-between gap-2">
                <HighlightedSnippet snippet={selected.snippet} query={query} />
                <Text size="xs" color="muted" className="self-end">
                  {pageHitLocator(selected)}
                </Text>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
